import { useRef, useState } from "react";
import { bucketUrlPrefix } from "common/config";
import { ReviewPhoto } from "common/types";
import { ChevronRight } from "lucide-react";
import VenuePlaceholderImage from "./VenuePlaceholderImage";

type CarouselImageSection = {
  imageMode: "carousel";
  photos: ReviewPhoto[];
  onPhotoClick: () => void;
  onPhotoScrolled?: (dir: "left" | "right") => void;
};

type StaticImageSection = {
  imageMode: "static";
  imageSrc: string;
  imageAlt: string;
  photoCount?: number;
  onImageClick: () => void;
  imageAriaLabel: string;
  moreLabel?: string;
};

type ImageSection = CarouselImageSection | StaticImageSection;

type AccessibilitySpaceCardProps = ImageSection & {
  title: string;
  titleClassName: string;
  subtitle?: React.ReactNode;
  /** Pre-computed display string — when provided, skips tagLines rendering entirely */
  tagsDisplayText?: string;
  tagLines?: string[];
  emptyTagMessage?: string;
  tagsCount: number;
  onContentClick: () => void;
  contentAriaLabel: string;
  viewAllLabel?: string;
  accessibilityTagsLabel?: string;
};

// ---------- Carousel sub-component ----------

function CarouselImageSection({
  photos,
  onPhotoClick,
  onPhotoScrolled,
  title,
}: CarouselImageSection & { title: string }) {
  const photoScrollContainerRef = useRef<HTMLDivElement>(null);
  const [currentPhotoIndex, setCurrentPhotoIndex] = useState(0);

  const handleScrollLeft = () => {
    onPhotoScrolled?.("left");
    if (photoScrollContainerRef.current && photos.length > 0) {
      const container = photoScrollContainerRef.current;
      const scrollAmount = container.clientWidth;
      setCurrentPhotoIndex((prev) => {
        const newIndex = prev > 0 ? prev - 1 : photos.length - 1;
        const isLooping = prev === 0;
        container.scrollTo({
          left: newIndex * scrollAmount,
          behavior: isLooping ? "auto" : "smooth",
        });
        return newIndex;
      });
    }
  };

  const handleScrollRight = () => {
    onPhotoScrolled?.("right");
    if (photoScrollContainerRef.current && photos.length > 0) {
      const container = photoScrollContainerRef.current;
      const scrollAmount = container.clientWidth;
      setCurrentPhotoIndex((prev) => {
        const newIndex = prev < photos.length - 1 ? prev + 1 : 0;
        const isLooping = prev === photos.length - 1;
        container.scrollTo({
          left: newIndex * scrollAmount,
          behavior: isLooping ? "auto" : "smooth",
        });
        return newIndex;
      });
    }
  };

  if (photos.length === 0) {
    return (
      <button
        onClick={onPhotoClick}
        className="w-full h-full focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 cursor-pointer group relative overflow-hidden"
        aria-label={`View ${title} details`}
      >
        <VenuePlaceholderImage className="w-full h-full" />
        <div className="absolute inset-0 bg-[#070708] opacity-0 group-hover:opacity-[0.12] group-active:opacity-[0.12] transition-opacity duration-200 pointer-events-none rounded" />
      </button>
    );
  }

  return (
    <>
      {/* Navigation buttons — only when more than 1 photo */}
      {photos.length > 1 && (
        <>
          <button
            type="button"
            onClick={handleScrollLeft}
            className="flex absolute left-2 top-1/2 -translate-y-1/2 z-10 items-center justify-center w-8 h-8 bg-container-brand-minimal border border-border-token-brand-minimal rounded-full hover:bg-bg-brand-ghost-hover transition-all focus-blue shadow-md hover:shadow-lg"
            aria-label="Previous photo"
          >
            <ChevronRight size={16} className="rotate-180 text-fg-neutral-strong" />
          </button>
          <button
            type="button"
            onClick={handleScrollRight}
            className="flex absolute right-2 top-1/2 -translate-y-1/2 z-10 items-center justify-center w-8 h-8 bg-container-brand-minimal border border-border-token-brand-minimal rounded-full hover:bg-bg-brand-ghost-hover transition-all focus-blue shadow-md hover:shadow-lg"
            aria-label="Next photo"
          >
            <ChevronRight size={16} className="text-fg-neutral-strong" />
          </button>
        </>
      )}

      {/* Snap carousel */}
      <div className="overflow-hidden w-full h-full">
        <div
          ref={photoScrollContainerRef}
          className="flex w-full h-full overflow-x-auto overflow-y-hidden scrollbar-hide snap-x snap-mandatory"
          role="list"
          aria-label={`${title} photos`}
        >
          {photos.map((photo, index) => (
            <div
              key={`photo-${index}`}
              className="flex-shrink-0 w-full h-full snap-center"
              role="listitem"
            >
              <button
                onClick={onPhotoClick}
                className="w-full h-full p-0 block focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 cursor-pointer group relative overflow-hidden"
                aria-label={`View ${title} details - Photo ${index + 1} of ${photos.length}${photo.alt_text ? `: ${photo.alt_text}` : ""}`}
                tabIndex={index === currentPhotoIndex ? 0 : -1}
              >
                <img
                  src={bucketUrlPrefix + photo.path}
                  alt=""
                  aria-hidden="true"
                  className="w-full h-full object-cover block"
                />
                <div className="absolute inset-0 bg-[#070708] opacity-0 group-hover:opacity-[0.12] group-active:opacity-[0.12] transition-opacity duration-200 pointer-events-none" aria-hidden="true" />
              </button>
            </div>
          ))}
        </div>
      </div>

      {/* Counter badge + screen-reader live region */}
      {photos.length > 1 && (
        <>
          <div
            className="absolute bottom-2 left-1/2 -translate-x-1/2 bg-black bg-opacity-70 text-white body-s-regular px-2 py-1 rounded z-20 pointer-events-none"
            aria-hidden="true"
          >
            {currentPhotoIndex + 1}/{photos.length}
          </div>
          <div aria-live="polite" aria-atomic="true" className="sr-only">
            {`Photo ${currentPhotoIndex + 1} of ${photos.length}${photos[currentPhotoIndex]?.alt_text ? `: ${photos[currentPhotoIndex].alt_text}` : ""}`}
          </div>
        </>
      )}
    </>
  );
}

// ---------- Static image sub-component ----------

function StaticImageSection({
  imageSrc,
  imageAlt,
  photoCount,
  onImageClick,
  imageAriaLabel,
  moreLabel = "more",
}: StaticImageSection) {
  return (
    <button
      onClick={onImageClick}
      className="w-full h-full focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 rounded cursor-pointer group relative overflow-hidden"
      aria-label={imageAriaLabel}
    >
      <img
        alt={imageAlt}
        className="w-full h-full object-cover"
        src={imageSrc}
      />
      <div className="absolute inset-0 bg-[#070708] opacity-0 group-hover:opacity-[0.12] group-active:opacity-[0.12] transition-opacity duration-200 pointer-events-none rounded" />
      {photoCount !== undefined && photoCount > 1 && (
        <div className="absolute bottom-2 right-2 bg-black bg-opacity-70 text-white body-s-regular px-2 py-1 rounded z-10">
          +{photoCount - 1} {moreLabel}
        </div>
      )}
    </button>
  );
}

// ---------- Main card component ----------

function AccessibilitySpaceCard(props: AccessibilitySpaceCardProps) {
  const {
    title,
    titleClassName,
    subtitle,
    tagsDisplayText,
    tagLines = [],
    emptyTagMessage = "",
    tagsCount,
    onContentClick,
    contentAriaLabel,
    viewAllLabel = "View all",
    accessibilityTagsLabel = "accessibility tags",
  } = props;

  const tagsText =
    tagsDisplayText !== undefined
      ? tagsDisplayText
      : tagLines.length === 0
        ? emptyTagMessage
        : tagLines.map((tag) => `• ${tag}`).join(" ");

  return (
    <article className="bg-bg-brand-secondary-default rounded-xl border border-border-token-brand-minimal overflow-hidden">
      <div className="flex flex-col card-wide:flex-row">
        {/* Image section */}
        <div className="w-full h-48 card-wide:w-[200px] card-wide:h-[166px] flex-shrink-0 relative">
          {props.imageMode === "carousel" ? (
            <CarouselImageSection
              imageMode="carousel"
              photos={props.photos}
              onPhotoClick={props.onPhotoClick}
              onPhotoScrolled={props.onPhotoScrolled}
              title={title}
            />
          ) : (
            <StaticImageSection
              imageMode="static"
              imageSrc={props.imageSrc}
              imageAlt={props.imageAlt}
              photoCount={props.photoCount}
              onImageClick={props.onImageClick}
              imageAriaLabel={props.imageAriaLabel}
              moreLabel={props.moreLabel}
            />
          )}
        </div>

        {/* Content button */}
        <button
          type="button"
          onClick={onContentClick}
          className="flex-1 flex flex-col gap-3 py-3 px-4 overflow-hidden text-left focus-blue group hover:bg-bg-brand-ghost-hover active:bg-bg-brand-ghost-hover transition-colors duration-200"
          aria-label={contentAriaLabel}
        >
          {/* Title & subtitle */}
          <div className="flex flex-col card-wide:flex-row card-wide:items-start card-wide:justify-between">
            <div className="flex-1">
              <h3 className={`${titleClassName} text-fg-neutral-strong mb-1`}>
                {title}
              </h3>
              {subtitle && (
                <div className="label-s-medium text-fg-neutral-subtle">
                  {subtitle}
                </div>
              )}
            </div>
          </div>

          {/* Tags preview */}
          <p className="label-m-medium text-fg-neutral-strong line-clamp-2">
            {tagsText}
          </p>

          {/* Separator + CTA — grouped with mt-auto so they always anchor to the bottom */}
          <div className="mt-auto flex flex-col gap-3">
            <hr className="border-border-token-brand-minimal" aria-hidden="true" />
            <div className="flex items-center gap-2" aria-hidden="true">
              <span className="label-m-medium text-fg-brand-subtle group-hover:text-fg-brand-strong group-active:text-fg-brand-strong border-b border-transparent group-hover:border-border-token-brand-subtle group-active:border-border-token-brand-strong transition-all duration-200">
                {viewAllLabel} {tagsCount} {accessibilityTagsLabel}
              </span>
              <svg
                className="w-5 h-5 transition-transform group-hover:translate-x-0.5 text-fg-brand-subtle group-hover:text-fg-brand-strong group-active:text-fg-brand-strong"
                fill="currentColor"
                viewBox="0 0 24 24"
                aria-hidden="true"
              >
                <path d="M12 4l-1.41 1.41L16.17 11H4v2h12.17l-5.58 5.59L12 20l8-8z" />
              </svg>
            </div>
          </div>
        </button>
      </div>
    </article>
  );
}

export default AccessibilitySpaceCard;
